import {
	Component, HostBinding, OnDestroy, ViewEncapsulation, ChangeDetectorRef, ViewChild, ElementRef,
	TemplateRef, ViewContainerRef, AfterViewInit
} from '@angular/core';
import { Overlay, OverlayConfig, ConnectionPositionPair, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { MediaMatcher } from '@angular/cdk/layout';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	Router,
	NavigationEnd,
	ActivatedRoute,
	NavigationStart,
	NavigationCancel,
	NavigationError,
	RouterLink,
	RouterLinkActive,
	RouterOutlet
} from '@angular/router';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';

import { fromMediaQueryListEvent } from '../../utils/media-query';
import { pageTransitions } from '../../animations/page-transitions';
import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { ConnectionService } from '../../services/connection.service';
import { LocationService } from '../../services/location.service';
import { OLD_STEELUSER_URL } from '../../../config';
import { AppWizardService } from '../../../offer/services/app-wizard.service';
import { AllQualityIssue } from '../../../claim/models/all-quality-issue';
import { AllQualityIssueService } from '../../../claim/services/all-quality-issue.service';
import { GuaranteeRequestService } from '../../../guarantee/services/guarantee-request.service';
import { Guarantee } from '../../../guarantee/models/guarantee';
import {
	DialogNewOfferRequestComponent
} from '../../../offer/components/utils/dialog-new-offer-request/dialog-new-offer-request.component';
import { DialogErrorComponent } from '../error-dialog/dialog-error.component';
import { TranslationService } from '../../services/translation.service';
import { UserCardService } from '../../../user-management/services/user-card.service';
import { OrganizationService } from '../../../user-management/services/organization.service';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { HelpDataComponent } from '../help/help-data.component';

@Component({
	selector: 'copo-skeleton',
	templateUrl: './skeleton.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './skeleton.component.css',
	imports: [
		MatToolbarModule,

		MatButtonModule,
		MatIconModule,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,
		MatRippleModule,

		MatTooltipModule,
		RouterLink,
		MatSidenavModule,

		MatListModule,
		RouterLinkActive,
		RouterOutlet,
		LoadingSpinnerComponent
	]
})
export class SkeletonComponent implements OnDestroy, AfterViewInit {
	@HostBinding('class.passthrough') readonly passthrough = true;

	@ViewChild('userCardContent', { static: false }) templateUserCardContent!: TemplateRef<HTMLElement>;
	@ViewChild('userHeader', { static: false, read: ElementRef }) userHeaderElement!: ElementRef<HTMLElement>;
	@ViewChild('helpButton', { static: false, read: ElementRef }) helpButtonElement!: ElementRef<HTMLElement>;
	user: User;
	mobileQuery: MediaQueryList;
	mobileQueryListSubscription: Subscription;
	userCardPortal!: TemplatePortal<HTMLElement>;
	helpCardPortal!: ComponentPortal<HelpDataComponent>;
	overlayRef: OverlayRef | undefined;
	backDropSubscription: Subscription | undefined;
	forbiddenAccessSubscription: Subscription;

	menuOpened: boolean;
	searchTextControl = new FormControl('', { nonNullable: true });
	navigationLoading = false;

	readonly oldSteelUserUrl = OLD_STEELUSER_URL;

	firstNameSubscription: Subscription;
	lastNameSubscription: Subscription;
	orgNameSubscription: Subscription;
	private readonly routerSubscription: Subscription;
	private readonly searchTextSubscription: Subscription;

	constructor(
		private readonly overlay: Overlay,
		private readonly viewContainerRef: ViewContainerRef,
		private readonly userService: UserService,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly media: MediaMatcher,
		private readonly connectionService: ConnectionService,
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		private readonly locationService: LocationService,
		private readonly appWizardService: AppWizardService,
		private readonly claimService: AllQualityIssueService,
		private readonly guaranteeRequestService: GuaranteeRequestService,

		private readonly dialog: MatDialog,
		private readonly translationService: TranslationService,
		private readonly userCardService: UserCardService,
		private readonly organizationService: OrganizationService
	) {
		this.firstNameSubscription = this.userCardService.subscribeToFirstName({
			next: firstName => {
				if (firstName) {
					this.user.firstname = firstName;
				}
			}
		});
		this.lastNameSubscription = this.userCardService.subscribeToLastName({
			next: lastName => {
				if (lastName) {
					this.user.lastname = lastName;
				}
			}
		});
		this.orgNameSubscription = this.organizationService.subscribeToOrgName({
			next: orgName => {
				if (orgName) {
					this.user.currentVisibility.organizationName = orgName;
				}
			}
		});
		this.user = this.userService.user;

		this.mobileQuery = this.media.matchMedia('screen and (width <= 1024px)');
		this.menuOpened = !this.mobileQuery.matches;
		this.mobileQueryListSubscription =
			fromMediaQueryListEvent(this.mobileQuery).subscribe(() => {
				if (this.mobileQuery.matches) {
					this.menuOpened = false;
				}
				this.changeDetectorRef.markForCheck();
			});
		this.routerSubscription = this.router.events
			.subscribe(evt => {
				if (evt instanceof NavigationStart) {
					this.navigationLoading = true;
					if (this.menuOpened && this.mobileQuery.matches) {
						this.menuOpened = false;
					}
				} else if (
					evt instanceof NavigationEnd
					|| evt instanceof NavigationCancel
					|| evt instanceof NavigationError
				) {
					this.navigationLoading = false;
				}

				if (!(evt instanceof NavigationEnd)) {
					return;
				}
				if (!this.router.isActive(
					'/search',
					{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
				)) {
					// reset search text when exiting 'search'
					if (this.searchTextControl.value !== '') {
						this.searchTextControl.setValue('');
					}
				} else {
					// update search text when starting 'search'
					const nodeSearch = this.activatedRoute.snapshot.firstChild;
					if (nodeSearch?.routeConfig && nodeSearch.routeConfig.path === 'search') {
						const searchString = nodeSearch.firstChild?.params['query']
							? decodeURIComponent(<string>nodeSearch.firstChild.params['query'])
							: '';
						const searchText = searchString.trim();
						if (this.searchTextControl.value !== searchText) {
							this.searchTextControl.setValue(searchText);
						}
					}
				}
			});

		// when searchText changed, then:
		// - if current URL isn't starting with 'search', then the user is redirected to /search
		// - else URL is updated with current searchText
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(async searchString => {
			const searchText = searchString.trim();
			if (!searchText) {
				return;
			}
			if (!this.router.isActive(
				'/search',
				{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
			)) {
				await this.router.navigate(['search', searchText]);
				return;
			}
			// update characters after '/search/' and, if any, before any sequence of '/' followed by any characters
			// e.g. '/search/a' replaced with 'ab' = '/search/ab'
			// e.g. '/search/a/orders' replaced with 'ab' = '/search/ab/orders'
			const updatedUrl = ['search'];
			const nodeSearch = this.activatedRoute.snapshot.firstChild;
			if (
				nodeSearch?.routeConfig && nodeSearch.routeConfig.path === 'search'
				&& nodeSearch.firstChild
			) {
				updatedUrl.push(searchText);
				let nextNode = nodeSearch.firstChild.firstChild;
				while (nextNode?.routeConfig?.path) {
					updatedUrl.push(...nextNode.url.map(v => v.path));
					nextNode = nextNode.firstChild;
				}
			}
			await this.router.navigate(updatedUrl, { replaceUrl: true });
		});
		this.forbiddenAccessSubscription = this.connectionService.getForbiddenAccessSubject().subscribe(data => {
			if (this.user.isSimulating() && this.user.readOnly && data) {
				const sub = this.dialog.open(DialogErrorComponent);
				sub.componentInstance.dialogError = this.translationService.getTR('readonly.user.error');
			}
		});
	}

	ngAfterViewInit() {
		this.userCardPortal = new TemplatePortal(this.templateUserCardContent, this.viewContainerRef);
		this.helpCardPortal = new ComponentPortal(HelpDataComponent);
	}

	evtGlobalSearchClosed() {
		this.locationService.go(-1, '');
	}

	openUserCardPopup() {
		this.cleanWhenLeaving();
		const positions = [
			new ConnectionPositionPair(
				{ originX: 'end', originY: 'bottom' },
				{ overlayX: 'end', overlayY: 'top' }
			)
		];
		const config = new OverlayConfig({
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
			positionStrategy: this.overlay.position().flexibleConnectedTo(this.userHeaderElement)
				.withDefaultOffsetX(-2)
				.withPositions(positions)
		});

		this.overlayRef = this.overlay.create(config);
		this.overlayRef.attach(this.userCardPortal);

		this.backDropSubscription = this.overlayRef.backdropClick().subscribe(() => {
			this.cleanWhenLeaving();
		});
	}

	openHelpCardPopup() {
		this.cleanWhenLeaving();
		const positions = [
			new ConnectionPositionPair(
				{ originX: 'end', originY: 'bottom' },
				{ overlayX: 'end', overlayY: 'top' }
			)
		];
		const config = new OverlayConfig({
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
			positionStrategy: this.overlay.position().flexibleConnectedTo(this.helpButtonElement)
				.withDefaultOffsetX(6)
				.withPositions(positions)
		});

		this.overlayRef = this.overlay.create(config);
		this.overlayRef.attach(this.helpCardPortal);

		this.backDropSubscription = this.overlayRef.backdropClick().subscribe(() => {
			this.cleanWhenLeaving();
		});
	}

	async changeSimulatedUser() {
		this.cleanWhenLeaving();
		await this.connectionService.requestLogout(false);
	}

	async changeDelegatedOrganization() {
		this.cleanWhenLeaving();
		await this.connectionService.requestLogout(false, { userId: this.user.id });
	}

	async logout() {
		this.cleanWhenLeaving();
		await this.connectionService.requestLogout();
	}

	onMenuOpenedChange(opened: boolean) {
		this.menuOpened = opened;
		// prevent error on IE11 (to not spam analytics with errors)
		if (/msie|trident.*rv[ :]*11\./gi.test(navigator.userAgent)) {
			return;
		}
		dispatchEvent(new Event('resize'));
	}

	ngOnDestroy() {
		this.mobileQueryListSubscription.unsubscribe();
		this.routerSubscription.unsubscribe();
		this.searchTextSubscription.unsubscribe();
		this.forbiddenAccessSubscription.unsubscribe();
		this.orgNameSubscription.unsubscribe();
		this.firstNameSubscription.unsubscribe();
		this.lastNameSubscription.unsubscribe();
		this.cleanWhenLeaving();
	}

	cleanWhenLeaving() {
		if (this.overlayRef) {
			this.overlayRef.detach();
			this.overlayRef = undefined;
		}
		if (this.backDropSubscription) {
			this.backDropSubscription.unsubscribe();
			this.backDropSubscription = undefined;
		}
	}

	async evtOnNewOfferFromList() {
		await this.appWizardService.actionNewOffer();
	}

	evtOnNewClaimFromList() {
		this.claimService.claim = new AllQualityIssue();
	}

	evtOnNewGuaranteeFromList() {
		this.guaranteeRequestService.guarantee = new Guarantee();
	}

	evtOnNewGuranteeFromList() {
		this.guaranteeRequestService.guarantee = new Guarantee();
	}

	evtRequestNewOffer() {
		this.dialog.open<DialogNewOfferRequestComponent, undefined, number>(DialogNewOfferRequestComponent, {
			panelClass: 'copo-offer-dialog', disableClose: true
		});
	}

	isAdmin() {
		const loginUser = this.user;
		// Admin check
		const currentVisibleOrgId = loginUser.currentVisibility.organizationId;
		const visibleOrgId = loginUser.visibleOrganizations;
		if (loginUser.isSimulating()) {
			return false;
		}
		return visibleOrgId.some(
			userOrg => userOrg.orgId === currentVisibleOrgId && userOrg.userRole === 'dsu'
		);
	}
}
